import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useSignupTestOrgLanding } from '@/hooks/useSignupTestOrgLanding';
import { getApiBaseUrl } from '@/lib/config';
import { hasMultiOrgFlag } from '@/lib/localStorage/multiOrgFlag';
import { logger } from '@/lib/logger';

import { useAuthInfo, useUser } from '../allauth_auth/hooks.js';
import { useOrg } from '../contexts/OrgContext';
import DashboardSkeleton from '../pages/dashboard/DashboardSkeleton';
import { OrganizationsPage } from './FullPageOrgSwitcher';

export function HomeRedirect() {
  const { isAuthenticated } = useAuthInfo();
  const user = useUser();
  const { currentOrg, availableOrgs, isLoading, error } = useOrg();
  // Allowlisted signup-test accounts get a freshly minted org on login
  // (apps/orgs/signals.py); this one-shot hint tells us to route them into it.
  const {
    data: signupTestLanding,
    isLoading: signupTestLandingLoading,
    isError: signupTestLandingError,
    error: signupTestLandingQueryError,
  } = useSignupTestOrgLanding(isAuthenticated);
  const navigate = useNavigate();

  // Check if user is known to have multiple orgs (from previous session)
  const getHasMultipleOrgsFromStorage = (): boolean => {
    return hasMultiOrgFlag(user?.id);
  };

  // Redirect superusers to Django admin panel
  useEffect(() => {
    if (isAuthenticated && user?.is_superuser) {
      logger.info('Superuser detected, redirecting to admin panel');
      const apiBaseUrl = getApiBaseUrl();
      window.location.href = `${apiBaseUrl}/`;
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    logger.debug('HomeRedirect debug', {
      isAuthenticated,
      isLoading,
      availableOrgs: availableOrgs.length,
      currentOrg: currentOrg?.name,
      error,
    });

    // Not authenticated - redirect to login immediately
    if (!isAuthenticated) {
      navigate('/account/login', { replace: true });
      return;
    }

    if (isLoading) {
      return; // Wait for team loading
    }

    if (error) {
      logger.error('Team loading error', new Error(error), {
        component: 'routing',
        location: 'HomeRedirect',
      });
      // Maybe show an error state instead of redirecting to create-team
      return;
    }

    if (signupTestLandingLoading) {
      return; // Wait for the one-shot landing hint before deciding where to go
    }

    if (signupTestLandingError) {
      logger.error(
        'Failed to fetch signup-test org landing hint, falling through to default routing',
        signupTestLandingQueryError ??
          new Error('signup-test-org-landing failed'),
        { component: 'routing', location: 'HomeRedirect', user_id: user?.id }
      );
      // Fall through to default org routing
    }

    // Allowlisted signup-test account: route straight into the freshly minted
    // org so onboarding runs, instead of the default org / team switcher.
    const signupTestOrgId = signupTestLanding?.org_id;
    if (signupTestOrgId != null) {
      logger.debug('Routing to signup-test org', {
        component: 'routing',
        location: 'HomeRedirect',
        org_id: signupTestOrgId,
        user_id: user?.id,
      });
      navigate(`/${signupTestOrgId}`, { replace: true });
      return;
    }

    if (availableOrgs.length === 0) {
      logger.debug('No teams available, redirecting to create-team');
      // No teams - redirect to team creation or team switcher if user can't create orgs
      if (!user || user.is_staff === undefined) {
        return;
      }
      navigate(
        user.is_staff === true ? '/create-organization' : '/organizations',
        { replace: true }
      );
    } else if (currentOrg) {
      navigate(`/${currentOrg.id}`, { replace: true });
    } else {
      logger.debug(
        'Multiple teams but no current team selected, showing team switcher'
      );
    }
  }, [
    isAuthenticated,
    currentOrg,
    availableOrgs,
    isLoading,
    navigate,
    error,
    user,
    signupTestLanding,
    signupTestLandingLoading,
    signupTestLandingError,
    signupTestLandingQueryError,
  ]);

  // Show dashboard skeleton while loading teams (smooth transition to dashboard).
  // Also wait on the one-shot signup-test landing hint so we don't flash the
  // default org / team switcher before routing an allowlisted account into its
  // freshly minted org.
  if (isAuthenticated && (isLoading || signupTestLandingLoading)) {
    // Skip skeleton for known multi-org users - they'll see org selector when loading completes
    if (getHasMultipleOrgsFromStorage()) {
      return null;
    }
    return <DashboardSkeleton />;
  }

  // Navigating into a freshly minted signup-test org — don't flash the default
  // dashboard / team switcher while the redirect is in flight.
  if (isAuthenticated && signupTestLanding?.org_id != null) {
    return null;
  }

  // Multiple teams but no current team selected - show full-page team switcher
  if (isAuthenticated && availableOrgs.length > 1 && !currentOrg) {
    return <OrganizationsPage />;
  }

  // Fallback - just return null to avoid flash during navigation
  return null;
}
